import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ListTree, Save, Lock, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { GroupBuilderAssign } from "@/components/CustomerMultiSelect";

const newSection = () => ({ title: "New Category", minimum: "", min_lm: 0, unit: "per/lm", description: "", note: "", items: [], external: [] });
const newRow = () => ({ size: "", price: 0 });
const newMaterial = () => ({ name: "", description: "", price: 0 });

export default function PriceList() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    api.get("/pricing").then((r) => setP(r.data));
    api.get("/customers").then((r) => setCustomers([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {});
  }, []);
  if (!p) return <div className="text-slate-400">Loading price list…</div>;

  const mutate = (fn) => setP((pp) => { const s = structuredClone(pp); fn(s); return s; });

  // Sections
  const updateSection = (si, key, val) => mutate((s) => {
    s.sections[si][key] = val;
    if (key === "min_lm") s.sections[si].minimum = Number(val) > 0 ? `Min ${Number(val)}lm` : "";
  });
  const addSection = () => mutate((s) => { s.sections = [...(s.sections || []), newSection()]; });
  const removeSection = (si) => mutate((s) => { s.sections.splice(si, 1); });
  const updateRow = (si, listKey, ii, key, val) => mutate((s) => { s.sections[si][listKey][ii][key] = key === "price" ? (parseFloat(val) || 0) : val; });
  const addRow = (si, listKey) => mutate((s) => { s.sections[si][listKey] = [...(s.sections[si][listKey] || []), newRow()]; });
  const removeRow = (si, listKey, ii) => mutate((s) => { s.sections[si][listKey].splice(ii, 1); });

  // Materials
  const updateMat = (mi, key, val) => mutate((s) => { s.materials[mi][key] = key === "price" ? (parseFloat(val) || 0) : val; });
  const addMat = () => mutate((s) => { s.materials = [...(s.materials || []), newMaterial()]; });
  const removeMat = (mi) => mutate((s) => { s.materials.splice(mi, 1); });

  // Notes
  const updateNote = (ni, val) => mutate((s) => { s.notes[ni] = val; });
  const addNote = () => mutate((s) => { s.notes = [...(s.notes || []), ""]; });
  const removeNote = (ni) => mutate((s) => { s.notes.splice(ni, 1); });

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...body } = p;
      body.notes = (body.notes || []).filter((n) => (n || "").trim());
      const r = await api.put("/pricing", body);
      setP(r.data);
      toast.success("Price list saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const rowEditor = (si, listKey, label) => {
    const rows = p.sections[si][listKey] || [];
    return (
      <div className="mt-3">
        {label && <p className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">{label}</p>}
        <div className="space-y-2">
          {rows.map((it, ii) => (
            <div key={ii} className="flex items-center gap-2" data-testid={`section-${si}-${listKey}-${ii}`}>
              <Input value={it.size} onChange={(e) => updateRow(si, listKey, ii, "size", e.target.value)} placeholder="Size / description" className="flex-1 h-8" />
              <span className="text-slate-400">$</span>
              <Input type="number" step="0.01" value={it.price} onChange={(e) => updateRow(si, listKey, ii, "price", e.target.value)} className="w-24 h-8" />
              <button onClick={() => removeRow(si, listKey, ii)} className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50" data-testid={`remove-${si}-${listKey}-${ii}`}><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <button onClick={() => addRow(si, listKey)} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-navy hover:underline" data-testid={`add-row-${si}-${listKey}`}><Plus className="w-3.5 h-3.5" />Add {listKey === "external" ? "external " : ""}item</button>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy flex items-center gap-2"><ListTree className="w-8 h-8" />Price List</h1>
          <p className="text-slate-500 mt-1 flex items-center gap-1"><Lock className="w-4 h-4" />Master catalogue — admin only. Every charge code, rate, unit & minimum lives here.</p>
        </div>
        <Button data-testid="pricing-save-button" onClick={save} disabled={saving} className="bg-navy hover:bg-navy-light"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save Price List"}</Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm max-w-xs">
        <Label>Hourly Rate (weekly billing)</Label>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-slate-400">$</span>
          <Input data-testid="hourly-rate-input" type="number" value={p.hourly_rate} onChange={(e) => setP({ ...p, hourly_rate: parseFloat(e.target.value) || 0 })} />
          <span className="text-slate-400 text-sm">/hr</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {p.sections.map((s, si) => (
          <div key={si} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid={`price-section-${si}`}>
            <GroupBuilderAssign customers={customers} ids={s.customer_ids} alsoStandard={s.also_standard} testId={`section-builders-${si}`}
              onIds={(v) => updateSection(si, "customer_ids", v)} onAlsoStandard={(v) => updateSection(si, "also_standard", v)} />
            <div className="flex items-start justify-between gap-2">
              <Input value={s.title} onChange={(e) => updateSection(si, "title", e.target.value)} className="font-heading font-semibold text-navy text-base h-9 flex-1" placeholder="Category name" data-testid={`section-title-${si}`} />
              <button onClick={() => removeSection(si)} className="p-2 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 shrink-0" data-testid={`remove-section-${si}`}><Trash2 className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-3">
              <div>
                <Label className="text-xs text-slate-500">Unit</Label>
                <Input value={s.unit || ""} onChange={(e) => updateSection(si, "unit", e.target.value)} placeholder="per/lm" className="h-8 mt-1" data-testid={`section-unit-${si}`} />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Minimum (lm)</Label>
                <Input type="number" value={s.min_lm ?? 0} onChange={(e) => updateSection(si, "min_lm", parseFloat(e.target.value) || 0)} className="h-8 mt-1" data-testid={`section-minlm-${si}`} />
              </div>
            </div>

            <div className="mt-2">
              <Label className="text-xs text-slate-500">Description (optional)</Label>
              <Input value={s.description || ""} onChange={(e) => updateSection(si, "description", e.target.value)} className="h-8 mt-1" placeholder="e.g. Price available upon review" />
            </div>

            {rowEditor(si, "items", "Items")}

            <div className="mt-3">
              <Label className="text-xs text-slate-500">Note (optional)</Label>
              <Input value={s.note || ""} onChange={(e) => updateSection(si, "note", e.target.value)} className="h-8 mt-1" placeholder="e.g. External works +$1.00 p/lm" />
            </div>

            {rowEditor(si, "external", "External")}
          </div>
        ))}
      </div>

      <button onClick={addSection} data-testid="add-section" className="w-full border-2 border-dashed border-slate-200 rounded-2xl py-4 text-slate-500 hover:border-navy hover:text-navy transition-colors inline-flex items-center justify-center gap-2 font-medium"><Plus className="w-4 h-4" />Add Category</button>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="max-w-md">
          <GroupBuilderAssign customers={customers} ids={p.materials_customer_ids} alsoStandard={p.materials_also_standard} testId="materials-builders"
            onIds={(v) => setP({ ...p, materials_customer_ids: v })} onAlsoStandard={(v) => setP({ ...p, materials_also_standard: v })} />
        </div>
        <h3 className="font-heading font-semibold text-navy mb-3">Day Rates & Materials</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {p.materials.map((m, mi) => (
            <div key={mi} className="flex items-center gap-2 border border-slate-100 rounded-lg px-3 py-2" data-testid={`material-${mi}`}>
              <div className="flex-1 space-y-1">
                <Input value={m.name} onChange={(e) => updateMat(mi, "name", e.target.value)} placeholder="Name" className="h-8 font-medium" />
                <Input value={m.description || ""} onChange={(e) => updateMat(mi, "description", e.target.value)} placeholder="Description (optional)" className="h-7 text-xs" />
              </div>
              <span className="text-slate-400">$</span>
              <Input type="number" step="0.01" value={m.price} onChange={(e) => updateMat(mi, "price", e.target.value)} className="w-24 h-8" />
              <button onClick={() => removeMat(mi)} className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50" data-testid={`remove-material-${mi}`}><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <button onClick={addMat} data-testid="add-material" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-navy hover:underline"><Plus className="w-4 h-4" />Add material / day rate</button>
      </div>

      <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6">
        <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3">Notes</h4>
        <div className="space-y-2">
          {(p.notes || []).map((n, ni) => (
            <div key={ni} className="flex items-center gap-2" data-testid={`note-${ni}`}>
              <Input value={n} onChange={(e) => updateNote(ni, e.target.value)} className="h-8 flex-1" />
              <button onClick={() => removeNote(ni)} className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50" data-testid={`remove-note-${ni}`}><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <button onClick={addNote} data-testid="add-note" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-navy hover:underline"><Plus className="w-4 h-4" />Add note</button>
      </div>
    </div>
  );
}
